import { matchPath } from "react-router-dom";
import type { NavigationType } from "react-router-dom";
import routes from "./routes";
import type { RouteMotion, TransitionType } from "./types";

/**
 * 라우트 전환 모션 = 카드 push/pop 모델.
 *
 * 방향은 nav-type 으로만 결정한다:
 * - PUSH(전진) → slide/push (새 카드가 오른쪽에서 위로 슬라이드인, 기존 카드는 아래에서 왼쪽으로 30% 밀리며 어두워짐)
 * - POP(후퇴)  → slide/pop  (위 카드가 오른쪽으로 나가며 아래의 이전 카드를 드러냄, 이전 카드는 -30%→0 복귀·딤 해제)
 * - REPLACE    → fade       (로그인 리다이렉트·세션 복구 등 방향이 없는 전환)
 * 카드 위치·딤 규칙 자체는 src/styles/transitions/_card-motion.scss 가 정한다.
 *
 * route override(RouteConfig.transition)가 있으면 그것이 최우선.
 * 초기 로드는 nav-type 이 POP 이라 slide/pop 으로 계산되지만, page-transition.scss 가 appear 를
 * enter 와 따로 정지로 두므로 이동 없이 즉시 나타난다(네이티브 스플래시가 덮는 구간이라 자연스럽다).
 *
 * mode/dir 는 부모 컨테이너의 data-mode/data-nav 로 넘겨 CSS 캐스케이드가 애니메이션 시점에
 * "현재" 값을 읽게 한다 — CSSTransition classNames 는 항상 "route" 로 고정하므로, 나가는 자식이
 * 이전 렌더의 classNames(stale)를 들고 나가 잘못된 전환(예: 탭 전환 직후 fade-exit)이 걸리는
 * 문제가 원천 차단된다. 대가로 340ms 안에 연속 이동하면 아직 나가는 오래된 카드도 현재 방향을 읽는다
 * (page-transition.scss 머리 주석 참고 — 끝 상태 잔여는 없고, 그 구간은 알려진 한계).
 */
export function resolveRouteMotion(
  navType: NavigationType,
  override?: TransitionType,
): RouteMotion {
  const dir: RouteMotion["dir"] = navType === "POP" ? "pop" : "push";

  if (override === "none") return { mode: "none", dir };
  if (override === "fade") return { mode: "fade", dir };
  if (override === "slide-left") return { mode: "slide", dir: "push" };
  if (override === "slide-right") return { mode: "slide", dir: "pop" };

  if (navType === "REPLACE") return { mode: "fade", dir };
  return { mode: "slide", dir };
}

/**
 * 현재 경로에 매칭되는 라우트의 transition override 를 찾는다.
 * 동적 세그먼트(:id) 라우트도 매칭되도록 matchPath 사용. 탭 라우트(main-tabs)는
 * 호출처(Router.tsx)에서 isTabRoute 로 걸러내고 이 함수를 호출하지 않는다.
 */
export function findRouteTransition(pathname: string): TransitionType | undefined {
  const hit = routes.find((r) => matchPath(r.path, pathname));
  return hit?.transition;
}
